﻿import { useContext, useEffect } from 'react';

import { IntegrationsTab } from 'pages/partners/components/IntegrationsTab/IntegrationsTab';
import { PartnerData, PartnerDetailsContext, usePartners } from 'pages/partners/context';
import { LicensesTab } from 'pages/partners/components/LicensesTab/LicensesTab';
import { DetailsTab } from 'pages/partners/components/DetailsTab/DetailsTab';
import { OrdersTab } from 'pages/partners/components/OrdersTab/OrdersTab';
import { Partner, PartnerDetails, PartnerTypes } from 'apis/PartnerApi';
import { UsersTab } from 'pages/partners/components/UsersTab/UsersTab';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { useStickyTabIndex, useSuperState } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { License, LicenseApi } from 'apis/LicenseApi';
import { useNotification } from 'app/notifications';
import { useApp } from 'app';

interface PartnerProfileProps {
    Data: PartnerData;
    details: PartnerDetails;
    getPartnerTypeName: () => string;
    onDetailsItemClick: (dataItem: Partner, newPartner?: boolean) => Promise<void>;
}

export const PartnerProfile = (props: PartnerProfileProps) => {

    const { Data, details, getPartnerTypeName, onDetailsItemClick } = props;

    const [state, setState] = useSuperState({
        licenses: [] as License[],
        loadingOrderableLicenses: true,
        partnerCurrentLicenses: [] as License[]
    });
    const { licenses, loadingOrderableLicenses, partnerCurrentLicenses } = state;

    const { selectedPartner } = useApp();
    const { openTabIndex, pageContext, setSubTabIndex, subTabIndex } = useContext(PartnerDetailsContext);
    const { permissions } = usePartners();
    const { catchWithToast } = useNotification();
    const { formatMessage } = useIntl();

    // Remember the active sub-tab per partner across refreshes.
    useStickyTabIndex({
        entityId: pageContext?.Id,
        onRestore: index => {

            const copyOfSubTabIndex = [...subTabIndex];
            copyOfSubTabIndex[openTabIndex - 1] = index;
            setSubTabIndex(copyOfSubTabIndex);

        },
        scope: 'partner',
        value: subTabIndex[openTabIndex - 1] ?? 0
    });

    useEffect(() => {

        getAllLicenses();

    }, []);

    const getAllLicenses = async () => {

        const orderableLicenses = await LicenseApi.GetAll(1, 0, selectedPartner?.id ?? 0).catch(catchWithToast(formatMessage({ id: 'failed_to_load_orderable_licenses.24e84ec4', defaultMessage: 'Failed to load orderable licenses.' })));
        const currentLicenses = await LicenseApi.GetAll(1, details.ID).catch(catchWithToast(formatMessage({ id: 'failed_to_load_current_licenses.947b5810', defaultMessage: 'Failed to load current licenses.' })));

        if (orderableLicenses) {

            setState({
                licenses: orderableLicenses.filter(orderableLicense => {

                    if (currentLicenses) {

                        const alreadyExists = currentLicenses.find(currentLicense => currentLicense.ID === orderableLicense.ID);
                        if (!alreadyExists) {

                            return true;

                        } else {

                            return false;

                        }

                    } else {

                        return false;

                    }

                })
            });
            setState({ loadingOrderableLicenses: false });

            if (currentLicenses) {

                setState({ partnerCurrentLicenses: currentLicenses });

            }

        }

    };

    return (
        <GMetrixTabs
            unmountOnExit
            onSelect={i => {

                const copyOfSubTabIndex = [...subTabIndex];
                copyOfSubTabIndex[openTabIndex - 1] = i;
                setSubTabIndex(copyOfSubTabIndex);

            }}
            selected={subTabIndex[openTabIndex - 1]}
        >

            <GMetrixTabsTab
                title={(
                    <div className="joyride-details-tab">
                        <FormattedMessage defaultMessage="Details" description={formatMessage({ id: 'gridview_header.884261d1', defaultMessage: 'Gridview header' })} id="details.7799b923" />
                    </div>
                )}
            >
                <DetailsTab
                    getPartnerTypeName={getPartnerTypeName}
                    onDetailsItemClick={onDetailsItemClick}
                    partnerDetails={details}
                />
            </GMetrixTabsTab>

            <GMetrixTabsTab
                title={(
                    <div className="joyride-licenses-tab">
                        <FormattedMessage defaultMessage="Licenses" id="licenses.11b1c36a" />
                    </div>
                )}
            >
                <LicensesTab
                    getAllLicenses={getAllLicenses}
                    getPartnerTypeName={getPartnerTypeName}
                    licenses={licenses}
                    loadingOrderableLicenses={loadingOrderableLicenses}
                    partnerDetails={details}
                    partnersCurrentLicenses={partnerCurrentLicenses}
                    setLicenses={val => setState({ licenses: val })}
                    setLoadingOrderableLicenses={val => setState({ loadingOrderableLicenses: val })}
                    setPartnersCurrentLicenses={val => setState({ partnerCurrentLicenses: val })}
                />
            </GMetrixTabsTab>

            {permissions.OrderManagementFeatureFlag && (
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-orders-tab">
                            <FormattedMessage defaultMessage="Orders" id="orders.2fb22e52" />
                        </div>
                    )}
                >
                    <OrdersTab
                        details={details}
                        licenses={partnerCurrentLicenses}
                    />
                </GMetrixTabsTab>
            )}

            <GMetrixTabsTab
                title={(
                    <div className="joyride-users-tab">
                        <FormattedMessage defaultMessage="Users" id="users.581e673f" />
                    </div>
                )}
            >
                <UsersTab
                    partnerDetails={details}
                    {...Data}
                />
            </GMetrixTabsTab>

            {details.PartnerType === PartnerTypes.TestingCenter && (
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-integrations-tab">

                            <FormattedMessage defaultMessage="Integrations" id="integrations.89572b90" />
                        </div>
                    )}
                >
                    <IntegrationsTab partnerDetails={details} />
                </GMetrixTabsTab>
            )}

        </GMetrixTabs>
    );

};
